import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useBrand } from '../../app/config/brand';
import { useReauthRequired } from '../contexts/ReauthRequiredContext';
import { useAuthStatus } from '../../auth/useAuthStatus';

/**
 * Re-authenticate prompt for the one-login role-claim lag (R15).
 *
 * When the first-login invitation-resolution hook on the api-gateway auth path
 * grants a just-resolved invitee their platform role, it writes the role into
 * Auth0 `app_metadata` and signals the SPA via the `X-Reauth-Required` response
 * header (captured centrally in `useApi`'s `fetchWithAuth`). The role is now in
 * `app_metadata` but the *current* session's access token was minted before the
 * grant, so it does not yet carry the role — the user must sign in again to be
 * issued a fresh token. This prompt surfaces that instruction.
 *
 * R15 — `metaRole(u) != none and not sessionHasRole(u) -> reauthPrompt(u)` — is
 * the conjunction gated below: `reauthRequired` stands in for `metaRole != none`
 * (the server signalled the role was resolved) and `platformRole === null`
 * stands in for `not sessionHasRole`. Gating on `platformRole === null` makes the
 * prompt auto-hide the moment a re-issued token carries the role, and keeps it
 * hidden for an already-roled session (the "absent otherwise" half of R15).
 *
 * Unlike the blocking SessionExpiryModal, a roleless user can still use the app
 * as a customer, so this is a non-blocking banner rather than a full-screen modal.
 */
const ReauthPrompt: React.FC = () => {
  const { reauthRequired } = useReauthRequired();
  const { platformRole } = useAuthStatus();
  const { loginWithRedirect } = useAuth0();
  const brand = useBrand();

  if (!reauthRequired || platformRole !== null) return null;

  const handleReauth = () => {
    // A fresh authorization re-runs the Auth0 Post-Login Action, which reads the
    // freshly-written app_metadata role and mints a token that carries it.
    loginWithRedirect({
      appState: { returnTo: window.location.pathname + window.location.search },
    });
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[90] w-full max-w-md px-4"
      data-testid="reauth-prompt"
    >
      <div
        className="rounded-xl shadow-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3"
        style={{ backgroundColor: brand.colors.bgCard, border: `1px solid ${brand.colors.border}` }}
      >
        <div className="flex-1">
          <p className="text-sm font-semibold mb-1" style={{ color: brand.colors.secondary }}>
            Sign in again to finish setup
          </p>
          <p className="text-sm" style={{ color: brand.colors.textLight }}>
            Your new access was just granted. Please sign in again so your session picks up
            your updated permissions.
          </p>
        </div>

        <button
          onClick={handleReauth}
          className="shrink-0 px-5 py-2.5 text-sm font-medium rounded-full transition-all duration-200"
          style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
        >
          Sign In Again
        </button>
      </div>
    </div>
  );
};

export default ReauthPrompt;
